/**
 * LocalStorage Log Target
 * 
 * Persists logs to localStorage with configurable retention.
 */

import type { LogEntry, LogTarget } from '../types.js';

const STORAGE_KEY = 'pivot:logs';
const MAX_LOGS = 1000; // Default max logs to keep

/**
 * LocalStorage log target
 */
export class StorageTarget implements LogTarget {
  private maxLogs: number;

  constructor(maxLogs: number = MAX_LOGS) {
    this.maxLogs = maxLogs;
  }

  write(entry: LogEntry): void {
    try {
      const logs = this.getLogs();
      logs.push(entry);

      // Keep only the most recent logs
      if (logs.length > this.maxLogs) {
        logs.splice(0, logs.length - this.maxLogs);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
      // Storage may be full or disabled
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
    }
  }

  /**
   * Get all stored logs
   */
  getLogs(): LogEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (error) {
    }
    return [];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: string): LogEntry[] {
    return this.getLogs().filter(log => log.level === level);
  }

  /**
   * Get logs filtered by trace ID
   */
  getLogsByTrace(traceId: string): LogEntry[] {
    return this.getLogs().filter(log => log.traceId === traceId);
  }
}

